import {
    IWayPointCreationSingleVoyage,
    IWayPointCreationWithCorrectionsSingleVoyage
} from "@containers/singleVoyage/types/point";
import {
    useRootContextMutateSingleVoyage
} from "@containers/singleVoyage/mutateVoyage/context/rootContext/hooks/useRootContextMutateSingleVoyage";
import {useMapFeaturesContext} from "@ui/leafletCustom/context/mapContext/hooks/useMapFeaturesContext";
import {
    useViewContextMutateSingleVoyage
} from "@containers/singleVoyage/mutateVoyage/context/viewContext/hooks/useViewContextMutateSingleVoyage";
import {LatLng} from "leaflet";
import {
    OnChangePositionMutateSingleVoyage,
    OnClickPointMutateSingleVoyage,
    onDeletePointCbMutateSingleVoyage,
    onSortPointCbMutateSingleVoyage
} from "@containers/singleVoyage/mutateVoyage/points/types/events";
import {getLatLngFromPointGeomCoordinates} from "@helpers/geoJson/getLatLngFromPointGeomCoordinates";
import {uniq} from "lodash";
import {
    useRebuildPointsAndPositionsMutateSingleVoyage
} from "@containers/singleVoyage/mutateVoyage/points/hooks/useRebuildPointsAndPositionsMutateSingleVoyage";
import {
    reorderPointsMutateSingleVoyage
} from "@containers/singleVoyage/mutateVoyage/position/helpers/reoderPointsAndPositions";
import {
    deletePointsCorrectionsOnReorderMutateSingleVoyage
} from "@containers/singleVoyage/mutateVoyage/position/helpers/deleteCreationWithCorrectionsOnReorder";
import {useCallback, useState} from "react";
import {reverseGeocoding} from "@containers/reverseGeocoding/api";
import {buildNameReverseGeocoding} from "@containers/reverseGeocoding/helpers/buildNameReverseGeocoding";
import {buildAddressReverseGeocoding} from "@containers/reverseGeocoding/helpers/buildAddressReverseGeocoding";
import {getPointGeomCoordinatesFromLatLng} from "@helpers/geoJson/getPointGeomCoordinatesFromLatLng";
import {useShowError} from "@hooks/useShowError/useShowError";

export function usePointActionMutateSingleVoyage(): {
    isLoading: boolean,
    onDelete: onDeletePointCbMutateSingleVoyage,
    onSort: onSortPointCbMutateSingleVoyage,
    onClickPoint: OnClickPointMutateSingleVoyage
    onChangePosition: OnChangePositionMutateSingleVoyage
    onChangeTargetPoint: OnClickPointMutateSingleVoyage
} {

    const [isLoading, setIsLoading] = useState(false)

    const {
        points,
        targetPointId,
        setTargetPointId,
        tasks,
        setTasksLocalDeleteIds,
        setTasks,
        tasksLocalCreateIds,
    } = useRootContextMutateSingleVoyage()

    const {onShowError} = useShowError()

    const {mapFeatures} = useMapFeaturesContext()

    const {
        setModeSecondarySidebar,
    } = useViewContextMutateSingleVoyage()

    const {
        onRebuild,
        isPending,
    } = useRebuildPointsAndPositionsMutateSingleVoyage()


    const onDelete = async ({point, index}: {
        point: IWayPointCreationWithCorrectionsSingleVoyage,
        index: number
    }) => {
        const removedId = point?.id?.toString()
        const stripPoint = (waypointIds: number[] = []) => waypointIds.filter((id) => id?.toString() !== removedId)

        // A task without stops does not exist: if this was the only one, the task goes
        // away with the stop, and an already saved one must be deleted on the backend.
        const onlyUsedPointTasks = tasks.filter((task) => stripPoint(task.waypointIds).length === 0)

        if (onlyUsedPointTasks?.length >= 1) {
            const localDeleteIds: number[] = onlyUsedPointTasks.filter((task) => {
                return !tasksLocalCreateIds.includes(Number(task.id))
            })?.map((task) => task.id)
            if (localDeleteIds?.length) {
                setTasksLocalDeleteIds((prev) => {
                    return uniq([
                        ...prev,
                        ...localDeleteIds,
                    ])
                })
            }
        }

        setTasks((prev) => {
            return prev.filter((oldTask) => {
                return !onlyUsedPointTasks.some((task) => task.id?.toString() === oldTask.id?.toString())
            })?.map((task) => {
                return {
                    ...task,
                    waypointIds: stripPoint(task.waypointIds),
                    // Subtasks are bound to stops as well. Left uncleaned, on save the
                    // backend receives the id of a stop that is no longer in the voyage
                    // and rejects the whole task. A subtask left without stops goes away -
                    // it only ever lived on this one.
                    subtasks: (task.subtasks ?? [])
                        .map((subtask) => ({
                            ...subtask,
                            waypointIds: stripPoint(subtask.waypointIds),
                        }))
                        .filter((subtask) => subtask.waypointIds.length >= 1),
                }
            })
        })

        const newPoints = points.filter((item) => point?.id?.toString() !== item?.id?.toString())

        await onRebuild({
            points: newPoints
        })

        if (point?.id?.toString() === targetPointId?.toString()) {
            setTargetPointId(null)
            setModeSecondarySidebar('hide')
        }
    }


    const onSort = async ({
                              oldIndex,
                              newIndex
                          }: {
        oldIndex: number,
        newIndex: number
    }) => {
        if (oldIndex === newIndex) {
            return
        }
        const newPoints = reorderPointsMutateSingleVoyage({
            points: deletePointsCorrectionsOnReorderMutateSingleVoyage({
                points,
                oldPointIndex: oldIndex,
                newPointIndex: newIndex
            }),
            oldPointIndex: oldIndex,
            newPointIndex: newIndex
        })

        await onRebuild({
            points: newPoints
        })
    }

    const onClickPoint = (point: IWayPointCreationSingleVoyage) => {
        setTargetPointId(point.id.toString())
        setModeSecondarySidebar('point')
        if (mapFeatures) {
            const {lat, lng} = getLatLngFromPointGeomCoordinates(point.geom)
            mapFeatures.setView(new LatLng(lat, lng,), 19)
        }
    }

    const onChangePosition: OnChangePositionMutateSingleVoyage = useCallback(async ({
                                                                                        latLng,
                                                                                        point,
                                                                                    }) => {
        setIsLoading(true)

        const oldLatLng = getLatLngFromPointGeomCoordinates(point.geom)

        try {
            const res = await reverseGeocoding({
                lat: latLng.lat,
                lon: latLng.lng
            }, onShowError)
            if (res?.error) {
                onShowError(res.error)
            }
            const newPoints: IWayPointCreationWithCorrectionsSingleVoyage[] = points.map((item) => {
                if (item.id?.toString() === point.id.toString()) {
                    return {
                        ...point,
                        name: buildNameReverseGeocoding(res),
                        address: buildAddressReverseGeocoding(res) ?? point.address,
                        geom: getPointGeomCoordinatesFromLatLng({
                            lat: res?.lat ? latLng.lat : oldLatLng.lat,
                            lng: res?.lon ? latLng.lng : oldLatLng.lng,
                        }),
                    }
                }
                return item;
            })

            await onRebuild({
                points: newPoints,
            })
            setTargetPointId(point.id?.toString())
            setModeSecondarySidebar('point')
            setIsLoading(false)
        } catch (e) {
            setIsLoading(false)
        }

    }, [points])

    const onChangeTargetPoint: OnClickPointMutateSingleVoyage = useCallback((point) => {
        setTargetPointId(point.id?.toString())
        setModeSecondarySidebar('point')
    }, [])

    return {
        onClickPoint,
        onSort,
        onDelete,
        onChangePosition,
        onChangeTargetPoint,
        isLoading: isPending || isLoading
    }
}
